import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
    getUserConsentApi,
    updateUserConsentApi,
    getUserDataApi,
} from "../../services/axios/AuthApis/UserMasterApis";
import ConsentForm from "./ConsentForm";
import { signOut } from "aws-amplify/auth";
import toast from "../../utils/notify";

const ConsentPage = () => {
    const { user, setUser, setIsAuthenticated } = useAuth();
    const navigate = useNavigate();
    const { state } = useLocation();
    const [loading, setLoading] = useState(true);
    const [hasConsent, setHasConsent] = useState(false);
    const [email, setEmail] = useState(user?.email || state?.username || localStorage.getItem('pendingUserEmail'));

    // if we arrived with username in state, ensure storage has it
    useEffect(() => {
        if (state?.username) {
            localStorage.setItem('pendingUserEmail', state.username);
        }
    }, [state]);

    // fetch consent status on load
    useEffect(() => {
        const fetchConsent = async () => {
            // determine email from context or pending storage
            const currentEmail = user?.email || email;
            if (!currentEmail) {
                return navigate("/login");
            }
            try {
                const res = await getUserConsentApi(currentEmail);
                const consent = res?.consent_given;
                if (consent) {
                    // user already agreed, fetch details and redirect
                    localStorage.setItem("userConsent", "true");
                    // optional: populate user context if missing
                    if (!user) {
                        const userdata = await getUserDataApi();
                        setUser(userdata);
                        setIsAuthenticated(true);
                    }
                    navigate("/central-dashboard", { replace: true });
                } else {
                    setHasConsent(false);
                }
            } catch (err) {
                console.error("Error fetching consent info:", err);
                setHasConsent(false);
            } finally {
                setLoading(false);
            }
        };
        fetchConsent();
    }, [user, navigate, email]);

    const handleAccept = async () => {
        setLoading(true);
        try {
            const payload = {
                email: user?.email || email,
                consent_given: true,
            };
            await updateUserConsentApi(payload);
            localStorage.setItem("userConsent", "true");
            toast.success("Consent accepted, redirecting...");
            // fetch and store user details then redirect
            const userdata = await getUserDataApi();
            setUser(userdata);
            setIsAuthenticated(true);
            localStorage.removeItem('pendingUserEmail');
            navigate("/central-dashboard", { replace: true });
        } catch (error) {
            console.error("Error updating consent:", error);
            toast.error("Failed to record consent. Please try again");
        } finally {
            setLoading(false);
        }
    };

    const handleDeny = async () => {
        setLoading(true);
        try {
            const payload = {
                email: user?.email || email,
                consent_given: false,
            };
            await updateUserConsentApi(payload);
            localStorage.setItem("userConsent", "false");
            // even if denied, we keep the user logged in but limited
            const userdata = await getUserDataApi();
            setUser(userdata);
            setIsAuthenticated(true);
            localStorage.removeItem('pendingUserEmail');
            toast('Consent denied. Redirecting to dashboard...');
            navigate("/central-dashboard", { replace: true });
        } catch (err) {
            console.error("Error denying consent:", err);
            toast.error("Unable to process your response. Try again later.");
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "#f8f9fc" }}>
                <style>{`@keyframes tkspin{to{transform:rotate(360deg)}}`}</style>
                <div style={{
                    width: 48, height: 48, borderRadius: "50%",
                    border: "3px solid #26256b22",
                    borderTopColor: "#26256b",
                    animation: "tkspin 0.7s linear infinite",
                }} />
            </div>
        );
    }

    return <ConsentForm onAccept={handleAccept} onDeny={handleDeny} user={user} loading={loading} />;
};

export default ConsentPage;